import type { InteractionAction, InteractionDefinition } from '../types';
import { normalizeInteractions } from '../models/interaction-schema';

function targetFor(root: Document, selector: string): HTMLElement | null {
  if (!selector) return null;
  try {
    const found = root.querySelector<HTMLElement>(selector);
    if (found) return found;
    const idMatch = selector.match(/\[data-wk-id=["']?([^"'\]]+)["']?\]/);
    if (idMatch?.[1]) {
      const byId = root.getElementById(idMatch[1]);
      if (byId) return byId;
      const byData = root.querySelector<HTMLElement>(`[data-webkiln-id="${idMatch[1]}"]`);
      if (byData) return byData;
    }
    return null;
  } catch {
    return null;
  }
}

let lastActiveElement: HTMLElement | null = null;

function closeDialog(root: Document, dialog: HTMLElement | null): void {
  if (!dialog) return;
  dialog.hidden = true;
  dialog.setAttribute('aria-hidden', 'true');
  dialog.removeAttribute('data-wk-open');
  if ('open' in dialog) {
    (dialog as HTMLDialogElement).open = false;
  }
  dialog.removeAttribute('open');
  if ('close' in dialog && typeof (dialog as HTMLDialogElement).close === 'function') {
    try {
      (dialog as HTMLDialogElement).close();
    } catch (_error) {
      void _error;
    }
  }
  const backdrop = root.querySelector<HTMLElement>('.wk-modal-backdrop, [data-wk-backdrop]');
  if (backdrop) {
    backdrop.hidden = true;
    backdrop.style.display = 'none';
  }
  if (root.body) root.body.style.overflow = '';
  if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
    try {
      lastActiveElement.focus();
    } catch (_error) {
      void _error;
    }
    lastActiveElement = null;
  }
}

function openDialog(root: Document, dialog: HTMLElement | null): void {
  if (!dialog) return;
  lastActiveElement = root.activeElement as HTMLElement | null;
  dialog.hidden = false;
  dialog.setAttribute('aria-hidden', 'false');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('data-wk-open', 'true');
  if (
    'showModal' in dialog &&
    typeof (dialog as HTMLDialogElement).showModal === 'function' &&
    dialog.tagName === 'DIALOG' &&
    !(dialog as HTMLDialogElement).open
  ) {
    try {
      (dialog as HTMLDialogElement).showModal();
    } catch (_error) {
      void _error;
    }
  }
  if ('open' in dialog) {
    (dialog as HTMLDialogElement).open = true;
  }
  dialog.setAttribute('open', '');
  let backdrop = root.querySelector<HTMLElement>('.wk-modal-backdrop, [data-wk-backdrop]');
  if (!backdrop && dialog.tagName !== 'ASIDE') {
    backdrop = root.createElement('div');
    backdrop.className = 'wk-modal-backdrop';
    backdrop.setAttribute('data-wk-backdrop', 'true');
    root.body?.appendChild(backdrop);
  }
  if (backdrop) {
    backdrop.hidden = false;
    backdrop.style.display = 'block';
  }
  if (root.body) root.body.style.overflow = 'hidden';
  const focusable = dialog.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  );
  if (focusable.length) {
    try {
      focusable[0].focus();
    } catch (_error) {
      void _error;
    }
  } else {
    dialog.tabIndex = -1;
    try {
      dialog.focus();
    } catch (_error) {
      void _error;
    }
  }
}

function applyAction(
  root: Document,
  action: InteractionAction,
  fallbackTarget: string | HTMLElement,
  reducedMotion: boolean,
): void {
  const element =
    fallbackTarget && typeof fallbackTarget === 'object' && 'nodeType' in (fallbackTarget as object)
      ? (fallbackTarget as HTMLElement)
      : targetFor(root, action.target || (typeof fallbackTarget === 'string' ? fallbackTarget : ''));
  if (!element) return;
  if (reducedMotion && action.reducedMotion === 'skip') return;
  const duration = reducedMotion && action.reducedMotion !== 'preserve' ? 0 : action.duration;
  const easing = action.easing ?? 'ease';

  if (action.type === 'show') element.hidden = false;
  if (action.type === 'hide') element.hidden = true;
  if (action.type === 'toggle-class' && typeof action.value === 'string')
    element.classList.toggle(action.value);
  if (action.type === 'open-modal') {
    const dialog =
      (action.target ? targetFor(root, action.target) : null) ??
      (element.matches('dialog, [role="dialog"]') ? element : null) ??
      root.querySelector<HTMLElement>('dialog.wk-modal, [role="dialog"], dialog');
    if (dialog) openDialog(root, dialog);
    return;
  }
  if (action.type === 'close-modal') {
    const dialog =
      (action.target ? targetFor(root, action.target) : null) ??
      (element.matches('dialog, [role="dialog"]') ? element : null) ??
      root.querySelector<HTMLElement>('[data-wk-open="true"], dialog.wk-modal, [role="dialog"], dialog');
    if (dialog) closeDialog(root, dialog);
    return;
  }
  if (action.type === 'open-drawer') {
    const drawer =
      (action.target ? targetFor(root, action.target) : null) ??
      (element.matches('.wk-drawer, [role="dialog"]') ? element : null) ??
      root.querySelector<HTMLElement>('.wk-drawer, [role="dialog"]');
    if (drawer) openDialog(root, drawer);
    return;
  }
  if (action.type === 'close-drawer') {
    const drawer =
      (action.target ? targetFor(root, action.target) : null) ??
      (element.matches('.wk-drawer, [role="dialog"]') ? element : null) ??
      root.querySelector<HTMLElement>('[data-wk-open="true"], .wk-drawer');
    if (drawer) closeDialog(root, drawer);
    return;
  }
  if (action.type === 'toggle-drawer') {
    const drawer =
      (action.target ? targetFor(root, action.target) : null) ??
      (element.matches('.wk-drawer, [role="dialog"]') ? element : null) ??
      root.querySelector<HTMLElement>('.wk-drawer, [role="dialog"]');
    if (drawer) {
      if (drawer.getAttribute('data-wk-open') === 'true' || !drawer.hidden) {
        closeDialog(root, drawer);
      } else {
        openDialog(root, drawer);
      }
    }
    return;
  }
  if (action.type === 'switch-tab') {
    const tab = element.getAttribute('role') === 'tab' ? element : element.closest('[role="tab"]');
    if (tab) {
      const list = tab.closest('[role="tablist"]');
      const tabsCont = tab.closest('.wk-tabs') || list?.parentElement;
      if (list) {
        const tabs = Array.from(list.querySelectorAll<HTMLElement>('[role="tab"]'));
        const panels = tabsCont
          ? Array.from(tabsCont.querySelectorAll<HTMLElement>('[role="tabpanel"]'))
          : [];
        tabs.forEach((t, i) => {
          const isSelected = t === tab;
          t.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          t.setAttribute('tabindex', isSelected ? '0' : '-1');
          t.classList.toggle('active', isSelected);
          const panelId = t.getAttribute('aria-controls');
          let panel = panelId ? root.getElementById(panelId) : null;
          if (!panel && panels[i]) {
            panel = panels[i];
          }
          if (panel) {
            panel.hidden = !isSelected;
            if (isSelected) {
              panel.removeAttribute('hidden');
              panel.style.display = '';
            } else {
              panel.setAttribute('hidden', '');
              panel.style.display = 'none';
            }
          }
        });
      }
    }
  }
  if (action.type === 'expand-accordion') {
    if (element instanceof HTMLDetailsElement) element.open = true;
    element.setAttribute('aria-expanded', 'true');
  }
  if (action.type === 'scroll-to')
    element.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' });
  if (
    action.type === 'opacity' ||
    action.type === 'translate' ||
    action.type === 'scale' ||
    action.type === 'rotate' ||
    action.type === 'color'
  ) {
    element.style.transition = `all ${duration}ms ${easing}`;
    const isActive = element.getAttribute('data-wk-active') === 'true';
    if (isActive) {
      element.removeAttribute('data-wk-active');
      element.style.transform = '';
      element.style.opacity = '';
      element.style.color = '';
      return;
    }
    element.setAttribute('data-wk-active', 'true');
    if (action.type === 'opacity')
      element.style.opacity = String(
        action.value ?? (element.style.opacity === '0.2' ? '1' : '0.2'),
      );
    if (action.type === 'translate')
      element.style.transform = `translate(${String(action.value ?? '40px, 0')})`;
    if (action.type === 'scale') element.style.transform = `scale(${String(action.value ?? 1.15)})`;
    if (action.type === 'rotate') {
      const raw = String(action.value ?? '15deg').trim();
      const val = /^-?\d+(\.\d+)?$/.test(raw) ? `${raw}deg` : raw;
      element.style.transform = `rotate(${val})`;
    }
    if (action.type === 'color') element.style.color = String(action.value ?? '#d6ad61');
  }
  if (action.type === 'counter') {
    const end = Number(
      action.value ?? element.getAttribute('data-wk-value') ?? element.textContent ?? 0,
    );
    const durAttr = Number(element.getAttribute('data-wk-duration'));
    const dur = duration || (durAttr > 0 ? durAttr : 1200);
    const start = performance.now();
    const tick = (now: number) => {
      const ratio = dur === 0 ? 1 : Math.min(1, (now - start) / dur);
      element.textContent = String(Math.round(end * ratio));
      if (ratio < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
  if (action.type === 'media-play' || action.type === 'media-pause') {
    const media =
      element instanceof HTMLMediaElement ? element : element.querySelector('video, audio');
    if (media instanceof HTMLMediaElement) {
      if (action.type === 'media-play') void media.play().catch(() => undefined);
      else media.pause();
    }
  }
}

export function resetInteractions(root: Document): void {
  root.querySelectorAll<HTMLElement>('.wk-counter, [data-wk-value]').forEach((el) => {
    el.textContent = '0';
  });
  root
    .querySelectorAll<HTMLElement>('[data-wk-open="true"], [aria-hidden="false"]')
    .forEach((el) => {
      el.removeAttribute('data-wk-open');
      el.setAttribute('aria-hidden', 'true');
      el.hidden = true;
    });
  root.querySelectorAll<HTMLElement>('*').forEach((el) => {
    el.removeAttribute('data-wk-active');
    if (el.style.transform) el.style.transform = '';
    if (el.style.transition) el.style.transition = '';
    if (el.style.opacity) el.style.opacity = '';
    if (el.style.color) el.style.color = '';
  });
}

export function runInteraction(root: Document, interaction: InteractionDefinition): void {
  const reducedMotion =
    typeof root.defaultView?.matchMedia === 'function'
      ? Boolean(root.defaultView.matchMedia('(prefers-reduced-motion: reduce)')?.matches)
      : false;
  const runs = interaction.loop.enabled
    ? Math.max(1, Math.min(10, interaction.loop.count ?? 2))
    : 1;
  const loopDelay = interaction.loop.delay ?? 0;
  const timerHost = root.defaultView ?? (typeof window !== 'undefined' ? window : globalThis);
  for (let run = 0; run < runs; run += 1) {
    interaction.actions.forEach((action, index) => {
      const offset =
        interaction.sequence === 'sequence'
          ? interaction.actions
            .slice(0, index)
            .reduce((sum, item) => sum + item.duration + item.delay, 0)
          : 0;
      timerHost.setTimeout(
        () => applyAction(root, action, interaction.target, reducedMotion),
        offset + action.delay + run * loopDelay,
      );
    });
  }
}

export function switchCarouselSlide(carousel: HTMLElement, targetIndex: number): void {
  const slides = Array.from(carousel.querySelectorAll<HTMLElement>('.wk-carousel-slide'));
  const dots = Array.from(carousel.querySelectorAll<HTMLElement>('.wk-carousel-dot'));
  if (!slides.length) return;
  const count = slides.length;
  const nextIdx = ((targetIndex % count) + count) % count;

  slides.forEach((slide, idx) => {
    const isTarget = idx === nextIdx;
    if (isTarget) {
      slide.classList.add('active');
      slide.hidden = false;
      slide.removeAttribute('hidden');
      slide.setAttribute('aria-hidden', 'false');
    } else {
      slide.classList.remove('active');
      slide.hidden = true;
      slide.setAttribute('hidden', '');
      slide.setAttribute('aria-hidden', 'true');
    }
  });

  dots.forEach((dot, idx) => {
    const isTarget = idx === nextIdx;
    if (isTarget) {
      dot.classList.add('active');
      dot.setAttribute('aria-selected', 'true');
      dot.tabIndex = 0;
    } else {
      dot.classList.remove('active');
      dot.setAttribute('aria-selected', 'false');
      dot.tabIndex = -1;
    }
  });

  const track = carousel.querySelector<HTMLElement>('.wk-carousel-track');
  if (track) {
    track.setAttribute('aria-live', 'polite');
  }
}

export function installInteractionRuntime(root: Document, definitions: unknown): () => void {
  const interactions = normalizeInteractions(definitions).filter((item) => item.enabled);
  const cleanups: Array<() => void> = [];
  const observer =
    typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          interactions
            .filter(
              (item) =>
                item.trigger === (entry.isIntersecting ? 'enter-viewport' : 'leave-viewport'),
            )
            .forEach((item) => runInteraction(root, item));
        });
      })
      : null;
  interactions.forEach((interaction) => {
    const element = targetFor(root, interaction.target);
    if (!element) return;
    if (interaction.trigger === 'enter-viewport' || interaction.trigger === 'leave-viewport')
      if (observer) observer.observe(element);
    if (interaction.trigger === 'page-load') runInteraction(root, interaction);
    if (interaction.trigger === 'click')
      element.addEventListener('click', () => runInteraction(root, interaction));
    if (interaction.trigger === 'hover')
      element.addEventListener('pointerenter', () => runInteraction(root, interaction));
    if (interaction.trigger === 'focus')
      element.addEventListener('focus', () => runInteraction(root, interaction));
    if (interaction.trigger === 'form-success')
      element.addEventListener('webkiln:form-success', () => runInteraction(root, interaction));
    if (observer) cleanups.push(() => observer.unobserve(element));
  });
  const scrollInteractions = interactions.filter((item) => item.trigger === 'scroll-position');
  const reducedMotion =
    typeof root.defaultView?.matchMedia === 'function'
      ? Boolean(root.defaultView.matchMedia('(prefers-reduced-motion: reduce)')?.matches)
      : false;

  root.querySelectorAll<HTMLElement>('.wk-counter, [data-wk-value]').forEach((el) => {
    const d = Number(el.getAttribute('data-wk-duration')) || 1200;
    applyAction(
      root,
      {
        id: 'auto-counter',
        type: 'counter',
        duration: d,
        delay: 0,
        easing: 'ease-out',
      },
      el,
      reducedMotion,
    );
  });

  root.querySelectorAll<HTMLElement>('.wk-carousel').forEach((carousel) => {
    const slides = Array.from(carousel.querySelectorAll<HTMLElement>('.wk-carousel-slide'));
    if (slides.length) {
      const activeIdx = slides.findIndex(
        (s) => s.classList.contains('active') || (!s.hidden && s.style.display !== 'none'),
      );
      switchCarouselSlide(carousel, activeIdx >= 0 ? activeIdx : 0);
    }
  });

  const onScroll = () => {
    const position = root.defaultView?.scrollY ?? 0;
    scrollInteractions
      .filter((item) => position >= (item.scrollPosition ?? 100))
      .forEach((item) => runInteraction(root, item));
  };
  const breakpointInteractions = interactions.filter(
    (item) => item.trigger === 'breakpoint-change',
  );
  const onResize = () => breakpointInteractions.forEach((item) => runInteraction(root, item));
  root.defaultView?.addEventListener('scroll', onScroll, { passive: true });
  root.defaultView?.addEventListener('resize', onResize);
  cleanups.push(() => root.defaultView?.removeEventListener('scroll', onScroll));
  cleanups.push(() => root.defaultView?.removeEventListener('resize', onResize));

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const activeEl = root.activeElement as HTMLElement | null;
      const carousel = activeEl?.closest<HTMLElement>('.wk-carousel');
      if (carousel) {
        event.preventDefault();
        const slides = Array.from(carousel.querySelectorAll<HTMLElement>('.wk-carousel-slide'));
        const currIdx = slides.findIndex(
          (s) => s.classList.contains('active') || (!s.hidden && s.style.display !== 'none'),
        );
        const safeIdx = currIdx < 0 ? 0 : currIdx;
        if (event.key === 'ArrowLeft') {
          switchCarouselSlide(carousel, safeIdx <= 0 ? slides.length - 1 : safeIdx - 1);
        } else {
          switchCarouselSlide(carousel, (safeIdx + 1) % slides.length);
        }
      }
    }
    if (event.key === 'Escape') {
      const openDialogEl = root.querySelector<HTMLElement>(
        '[data-wk-open="true"], [role="dialog"]:not([hidden]), dialog[open]',
      );
      if (openDialogEl) closeDialog(root, openDialogEl);
    }
    if (event.key === 'Tab') {
      const openDialogEl = root.querySelector<HTMLElement>(
        '[data-wk-open="true"], [role="dialog"]:not([hidden]), dialog[open]',
      );
      if (openDialogEl) {
        const focusable = Array.from(
          openDialogEl.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((item) => !item.hasAttribute('disabled'));
        if (focusable.length) {
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && root.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && root.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }
    }
  };
  root.addEventListener('keydown', onKeydown);
  cleanups.push(() => root.removeEventListener('keydown', onKeydown));

  const onClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const btn = target.closest<HTMLElement>('button, [role="button"], a');
    const openModalBtn =
      target.closest<HTMLElement>('[data-open-modal], [data-target="#modal-dialog"], .wk-open-modal') ??
      (btn && btn.textContent?.trim().toLowerCase().includes('open modal') ? btn : null);
    if (openModalBtn) {
      event.preventDefault();
      event.stopPropagation();
      const modalId = openModalBtn.getAttribute('data-open-modal');
      const dialog =
        (modalId ? root.getElementById(modalId) : null) ??
        openModalBtn.parentElement?.querySelector<HTMLElement>('dialog.wk-modal, [role="dialog"], dialog') ??
        root.querySelector<HTMLElement>('dialog.wk-modal, [role="dialog"], dialog');
      if (dialog) openDialog(root, dialog);
      return;
    }
    const openDrawerBtn =
      target.closest<HTMLElement>('[data-open-drawer], [data-target="#site-drawer"], .wk-open-drawer') ??
      (btn && btn.textContent?.trim().toLowerCase().includes('open drawer') ? btn : null);
    if (openDrawerBtn) {
      event.preventDefault();
      event.stopPropagation();
      const drawerId = openDrawerBtn.getAttribute('data-open-drawer');
      const drawer =
        (drawerId ? root.getElementById(drawerId) : null) ??
        openDrawerBtn.parentElement?.querySelector<HTMLElement>('.wk-drawer, [role="dialog"]') ??
        root.querySelector<HTMLElement>('.wk-drawer, [role="dialog"]');
      if (drawer) openDialog(root, drawer);
      return;
    }
    const closeBtn =
      target.closest<HTMLElement>('[data-close], [aria-label*="Close" i], [data-close-dialog], .wk-modal-close, .wk-drawer-close') ??
      (btn && btn.textContent?.trim().toLowerCase() === 'close' ? btn : null);
    if (closeBtn) {
      event.preventDefault();
      event.stopPropagation();
      const dialog =
        closeBtn.closest<HTMLElement>('[data-wk-open="true"], [role="dialog"], dialog') ??
        root.querySelector<HTMLElement>('[data-wk-open="true"], dialog[open], dialog.wk-modal');
      if (dialog) closeDialog(root, dialog);
      return;
    } else if (target.matches('.wk-modal-backdrop, [data-wk-backdrop]')) {
      event.preventDefault();
      event.stopPropagation();
      const dialog = root.querySelector<HTMLElement>(
        '[data-wk-open="true"], [role="dialog"], dialog[open], dialog.wk-modal',
      );
      if (dialog) closeDialog(root, dialog);
      return;
    }
    const cnt = target.closest<HTMLElement>('.wk-counter, [data-wk-value]');
    if (cnt) {
      applyAction(
        root,
        {
          id: 'click-counter',
          type: 'counter',
          duration: 1200,
          delay: 0,
          easing: 'ease-out',
        },
        cnt,
        false,
      );
    }
    const prevBtn = target.closest<HTMLElement>('.wk-carousel-prev, [aria-label*="Previous slide" i]');
    if (prevBtn) {
      event.preventDefault();
      event.stopPropagation();
      const carousel = prevBtn.closest<HTMLElement>('.wk-carousel');
      if (carousel) {
        const slides = Array.from(carousel.querySelectorAll<HTMLElement>('.wk-carousel-slide'));
        const currIdx = slides.findIndex(
          (s) => s.classList.contains('active') || (!s.hidden && s.style.display !== 'none'),
        );
        const safeIdx = currIdx < 0 ? 0 : currIdx;
        switchCarouselSlide(carousel, safeIdx <= 0 ? slides.length - 1 : safeIdx - 1);
      }
      return;
    }
    const nextBtn = target.closest<HTMLElement>('.wk-carousel-next, [aria-label*="Next slide" i]');
    if (nextBtn) {
      event.preventDefault();
      event.stopPropagation();
      const carousel = nextBtn.closest<HTMLElement>('.wk-carousel');
      if (carousel) {
        const slides = Array.from(carousel.querySelectorAll<HTMLElement>('.wk-carousel-slide'));
        const currIdx = slides.findIndex(
          (s) => s.classList.contains('active') || (!s.hidden && s.style.display !== 'none'),
        );
        const safeIdx = currIdx < 0 ? 0 : currIdx;
        switchCarouselSlide(carousel, (safeIdx + 1) % slides.length);
      }
      return;
    }
    const dotBtn = target.closest<HTMLElement>('.wk-carousel-dot, [data-slide-to]');
    if (dotBtn) {
      event.preventDefault();
      event.stopPropagation();
      const carousel = dotBtn.closest<HTMLElement>('.wk-carousel');
      const slideTo = dotBtn.getAttribute('data-slide-to');
      if (carousel && slideTo !== null) {
        switchCarouselSlide(carousel, parseInt(slideTo, 10) || 0);
      }
      return;
    }
    const tab = target.closest<HTMLElement>('[role="tab"]');
    if (tab) {
      applyAction(
        root,
        {
          id: 'tab-switch',
          type: 'switch-tab',
          duration: 0,
          delay: 0,
          easing: 'ease',
        },
        tab,
        false,
      );
      return;
    }
    const tooltip = target.closest<HTMLElement>('.wk-tooltip');
    if (tooltip) {
      const isOpen = tooltip.getAttribute('data-wk-open') === 'true';
      tooltip.setAttribute('data-wk-open', isOpen ? 'false' : 'true');
    } else {
      root.querySelectorAll('.wk-tooltip[data-wk-open="true"]').forEach((el) => {
        el.removeAttribute('data-wk-open');
      });
    }
  };
  root.addEventListener('click', onClick, true);
  cleanups.push(() => root.removeEventListener('click', onClick, true));
  root.addEventListener('click', onClick);
  cleanups.push(() => root.removeEventListener('click', onClick));

  if (observer) cleanups.push(() => observer.disconnect());
  return () => cleanups.forEach((cleanup) => cleanup());
}
